import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';

import {
  ChatOutlineIcon,
  EllipsisHorizontalIcon,
  HomeOutlineIcon,
  SearchOutlineIcon,
  SellOutlineIcon,
  SimpleTruckOutlineIcon,
} from '@/components/Icon';
import { colors } from '@/const/colors';
import { THEUNBAN } from '@/const/referralCode';
import { useChatContext } from '@/contexts/Chat/ChatContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import usePartnerSession from '@/hooks/usePartnerSession';
import { isExistNotReadChatMessageAtom } from '@/store/chatting';

type FooterProps = {
  isMain?: boolean;
};

const Footer = ({ isMain }: FooterProps) => {
  const { unreadMessages } = useChatContext();
  const authRedirect = useAuthRedirect();
  const [isExistNotReadChatMessage] = useAtom(isExistNotReadChatMessageAtom);
  const setIsExistNotReadChatMessage = useSetAtom(isExistNotReadChatMessageAtom);
  const isTheunban = usePartnerSession() === THEUNBAN;

  const menuList: Menu[] = isTheunban
    ? [
        {
          name: '내차사기',
          key: 'purchase',
          link: '/products',
          icon: SearchOutlineIcon,
        },
        {
          name: '채팅',
          key: 'chat',
          link: '/chatting',
          icon: ChatOutlineIcon,
        },
      ]
    : [
        {
          name: '홈',
          key: 'home',
          link: '/',
          icon: HomeOutlineIcon,
        },
        {
          name: '내차구매',
          key: 'purchase',
          link: '/products',
          icon: SearchOutlineIcon,
        },
        {
          name: '내차관리',
          key: 'sale',
          link: '/products/my',
          icon: SimpleTruckOutlineIcon,
        },
        {
          name: '채팅',
          key: 'chat',
          link: '/chatting',
          icon: ChatOutlineIcon,
        },
        {
          name: '더보기',
          key: 'more',
          link: '/more',
          icon: EllipsisHorizontalIcon,
        },
      ];

  useEffect(() => {
    const isEmpty = (obj: { [chatRoomId: number]: number }) => {
      return Object.keys(obj).length === 0;
    };

    if (!isEmpty(unreadMessages)) {
      setIsExistNotReadChatMessage(Object.values(unreadMessages).some((item) => item > 0));
    }
  }, [unreadMessages]);

  const handleMenuClick = (event: React.MouseEvent<HTMLAnchorElement>, menu: Menu) => {
    const authRequiredMenuItems = ['/products/my', '/chatting'];
    if (authRequiredMenuItems.includes(menu.link) && !authRedirect(menu.link, { feature: menu.name })) {
      event.preventDefault();
    }
  };

  const GRADIENT_ID = 'footer-blue-gradient-blue-to-blue';

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white w-full mx-auto my-0 max-w-[720px] min-w-[280px] text-sm">
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
          <defs>
            <linearGradient id={GRADIENT_ID} x1="100%" y1="55%" x2="0%" y2="45%">
              <stop offset="0.12%" stopColor="#535AFF" />
              <stop offset="50%" stopColor="#397AFF" />
              <stop offset="99.88%" stopColor="#10ACFF" />
            </linearGradient>
          </defs>
        </svg>
        {isMain && (
          <ul className="flex shadow-[0_-2px_4px_rgba(0,0,0,0.05)] py-2">
            {menuList.map((menu, index) => {
              const isActive = menu.link === window.location.pathname;
              const gradientTextStyle: React.CSSProperties = {
                backgroundImage: colors.gradient['blueGradient-blue-to-blue'],
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              };

              return (
                <li className={`text-center flex justify-center ${isTheunban ? 'w-1/2' : 'w-1/5'}`} key={menu.key}>
                  <a
                    href={menu.link}
                    onClick={(event) => handleMenuClick(event, menu)}
                    className={`flex flex-col justify-center items-center relative ${isActive ? 'active-link' : ''}`}
                    style={{
                      color: isActive ? undefined : 'gray',
                      fontWeight: isActive ? '600' : 'normal',
                    }}
                  >
                    <div className="relative">
                      {React.createElement(menu.icon, {
                        color: isActive ? `url(#${GRADIENT_ID})` : colors.gray[6],
                        strokeWidth: 0.1,
                        alt: `${menu.name} 아이콘`,
                        width: '28',
                        height: '28',
                      })}
                      {isExistNotReadChatMessage && menu.key === 'chat' && (
                        <div className="absolute top-0 right-[-4px] w-1 h-1 bg-red rounded"></div>
                      )}
                    </div>
                    <span style={isActive ? gradientTextStyle : undefined}>{menu.name}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </nav>
    </>
  );
};

export default Footer;
